import {Component, Input, OnChanges, OnDestroy, OnInit} from '@angular/core';
import {GoogleMap} from '@angular/google-maps';
import {Entity} from '../../../classes/entity';
import {Utils} from '../../../utils.class';
import {TtmTypes} from '../../../classes/ttm-types.enum';
import {TtmStatus} from '../../../classes/ttmstatus';

@Component({
    selector: 'app-svg-overlay',
    templateUrl: './svg-overlay.component.html',
    styleUrl: './svg-overlay.component.scss'
})
export class SvgOverlayComponent implements OnInit, OnDestroy, OnChanges {
    @Input() entity: Entity;
    @Input() map: GoogleMap;
    @Input() click: Function;
    @Input() downlight = false;
    @Input() gnd = false;
    @Input() zIndex = '100';
    @Input() ttmStatus: TtmStatus;
    // @ts-ignore
    private overlay: USGSOverlay;

    constructor() {
    }

    ngOnInit() {
        if (this.entity) {
            Utils.googleMapsCore().then(() => this.drawOverlay());
        }
    }

    drawOverlay() {
        class USGSOverlay extends google.maps.OverlayView {
            private bounds: google.maps.LatLngBounds;
            private image: string;
            private div?: HTMLElement;
            private overlayMouseTarget: Element;
            private hidden: boolean;
            private img?: HTMLImageElement;

            constructor(private entity: Entity,
                        private map: GoogleMap,
                        private divClick: Function,
                        private gnd = false,
                        private _downlight = false,
                        private zIndex = '100',
                        private shadowColor?: string) {
                super();
                this.setBounds();
                entity.updateBbox = () => {
                    this.setBounds();
                    this.draw();
                }
            }

            setBounds() {
                if (this.gnd) {
                    this.bounds = new google.maps.LatLngBounds(...Utils.convertGeometry(this.entity.gnd));
                    this.image = this.entity.gnd_image?.svg;
                } else {
                    this.bounds = new google.maps.LatLngBounds(...Utils.convertGeometry(this.entity.bbox));
                    this.image = this.entity.projected_image?.svg;
                }
            }

            private mapClickListener?: google.maps.MapsEventListener;

            override onAdd() {
                this.div = document.createElement('div');
                this.div.style.borderStyle = 'none';
                this.div.style.borderWidth = '0px';
                this.div.style.position = 'absolute';
                this.div.style.zIndex = this.zIndex;
                this.div.style.pointerEvents = 'none';
                this.div.className = 'svg-overlay';
                this.div.dataset['handle'] = `${this.entity.handle}`;
                const useImage = true;
                if (useImage) {
                    // Create the img element and attach it to the div.
                    this.img = document.createElement('img');

                    let blob = new Blob([this.image], {type: 'image/svg+xml'});
                    this.img.src = URL.createObjectURL(blob);

                    this.img.style.width = '100%';
                    this.img.style.height = '100%';
                    this.img.style.position = 'absolute';
                    this.img.style.pointerEvents = 'none';
                    this.img.draggable = false;
                    this.img.style.userSelect = 'none';
                    (this.img.style as any).webkitUserDrag = 'none';

                    this.div.appendChild(this.img);
                } else {
                    this.div.innerHTML = this.image;
                }

                const panes = this.getPanes()!;
                this.overlayMouseTarget = panes.overlayMouseTarget;
                this.overlayMouseTarget.appendChild(this.div);

                this.mapClickListener = this.getMap().addListener('click', this.overlayClick);

                this.downlight(this._downlight);
                this.applyShadowColor(this.shadowColor);
            }

            overlayClick = (event: google.maps.MapMouseEvent) => {
                if (this.hidden || !this.img?.complete) {
                    return; // Whole overlay is hidden or image not loaded yet, do nothing
                }

                // Calculate the x and y position of the click relative to the image
                const rect = this.img.getBoundingClientRect();
                const x = (event.domEvent as MouseEvent).clientX - rect.left;
                const y = (event.domEvent as MouseEvent).clientY - rect.top;

                if (x < 0 || y < 0 || x >= this.img.width || y >= this.img.height) {
                    return; // Click is outside the image, do nothing
                }

                // Create a canvas and draw the image onto it
                const canvas = document.createElement('canvas');
                const context = canvas.getContext('2d');
                canvas.width = this.img.width;
                canvas.height = this.img.height;
                context.drawImage(this.img, 0, 0, this.img.width, this.img.height);

                // Check the transparency of the clicked pixel
                const pixel = context.getImageData(x, y, 1, 1).data;
                if (pixel[3] !== 0 && this.divClick) {
                    this.divClick(this.entity.ttm_id); // Execute the divClick function if the pixel is not transparent
                }
            }

            applyShadowColor(shadowColor: string) {
                if (shadowColor && this.img) {
                    this.img.style.filter = `drop-shadow(3px 0 0 ${shadowColor}) drop-shadow(-3px 0 0 ${shadowColor}) drop-shadow(0 3px 0 ${shadowColor}) drop-shadow(0 -3px 0 ${shadowColor})`;
                }
            }

            downlight(doDownlight: boolean) {
                this._downlight = doDownlight;
                if (doDownlight) {
                    this.div?.classList.add('downlight');
                } else {
                    this.div?.classList.remove('downlight');
                }
            }

            override draw() {
                const overlayProjection = this.getProjection();
                // The call of draw() and onRemove() could cross each other causing a timing issue where the projection becomes empty
                if (overlayProjection) {
                    const sw = overlayProjection.fromLatLngToDivPixel(this.bounds.getSouthWest())!;
                    const ne = overlayProjection.fromLatLngToDivPixel(this.bounds.getNorthEast())!;

                    const showBeaconsZoom = this.map.zoom >= 18 || this.entity.ttm?.type_key !== TtmTypes.beacons_cones;
                    if (showBeaconsZoom) {
                        if (this.div) {
                            this.div.style.left = Math.min(sw.x, ne.x) + 'px';
                            this.div.style.top = Math.min(sw.y, ne.y) + 'px';
                            this.div.style.width = Math.abs(ne.x - sw.x) + 'px';
                            this.div.style.height = Math.abs(sw.y - ne.y) + 'px';
                            this.div.style.display = 'block';
                            this.hidden = false;
                        }
                    } else {
                        this.div.style.display = 'none';
                        this.hidden = true;
                    }
                }
            }

            override onRemove() {
                this.mapClickListener?.remove();
                if (this.div) {
                    (this.div.parentNode as HTMLElement).removeChild(this.div);
                    delete this.div;
                }
            }
        }


        this.overlay = new USGSOverlay(this.entity, this.map, this.click, this.gnd, this.downlight, this.zIndex, this.hexToRgba(this.ttmStatus?.color));
        this.overlay.setMap(this.map.googleMap);
    }

    hexToRgba(hex: string, alpha = 0.88): string {
        if (!hex?.length) {
            return '';
        }
        hex = hex.replace("#", "").trim();

        // #RGB -> #RRGGBB
        if (hex.length === 3) {
            hex = hex.split("").map(c => c + c).join("");
        }

        if (hex.length !== 6) {
            return '';
        }

        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);

        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    ngOnDestroy() {
        this.overlay.setMap(null);
        this.overlay.onRemove();
    }

    ngOnChanges() {
        this.overlay?.downlight(this.downlight);
        if (this.ttmStatus) {
            this.overlay?.applyShadowColor(this.hexToRgba(this.ttmStatus.color));
        }
    }
}
